import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Copy } from 'lucide-react';
import { Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { getRelayTokenPriceSolana } from '../../hooks/useRelayPrice';
import { getSolanaTokenByAddress } from '../../utils/supabase';
import { formatPriceDisplay } from '../../utils/priceFormatter';
import { fetchPwaAuthUser, type PwaAuthUser, type TelegramUser } from '../../utils/telegramAuth';
import {
  createSwapFeedConnection,
  fetchSwapFeedHistory,
  fetchSentryFeeSwapSummary,
  type SwapEvent,
  type SentryFeeSwapSummary
} from '../../utils/sentryBotApi';
import ComprehensiveAnalytics from '../../components/analytics/ComprehensiveAnalytics';
import Top5Groups from '../../components/groups/Top5Groups';
import TraderAnalytics from '../../components/trader/TraderAnalytics';
import UserGroupPortfolio from '../../components/portfolio/UserGroupPortfolio';
import { useSNSName } from '../../hooks/useSNSName';

const DEXSCREENER_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/DEXS.png';
const ORCA_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/ORCA-BUY.png';
const WEBSITE_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/WEB.png';
const TWITTER_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/X.png';
const TELEGRAM_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/TG.png';
const GITBOOK_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/Gitbook%20icon.png';
const LP_LOCK_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/LPLock%20Icon.png';
const TVL_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/TVL%20Icon.png';
const IFA_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/IFA%20Icon.png';
const PWA_SWAP_ICON_URL = '/pwa-swap-icon.png';
const TELEGRAM_SWAP_ICON_URL = '/telegram-swap-icon.png';
const SENTRY_SWAP_ICON_URL = '/sentry-swap-icon.png';
const SENTRY_BOT_API_URL = (import.meta.env as any).VITE_SENTRY_BOT_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:3001' : 'https://web-production-7d3e.up.railway.app');
const SENTRY_TOKEN_ADDRESS = '55592jXxdwmCxERy2YmpJMi7MGcqJ6kwYJ2Ztrro7XfX';
const SENTRY_FEES_WALLET = 'C9ocu16Z4ZhQ2irbUukmdr1y6W7eyXRYmBFu4SeBh2Ef';
const SENTRY_FEES_WALLET_SOLSCAN_URL = `https://solscan.io/account/${SENTRY_FEES_WALLET}#portfolio`;
const SENTRY_TOKEN_SOLSCAN_URL = `https://solscan.io/token/${SENTRY_TOKEN_ADDRESS}`;
const SENTRY_GITBOOK_URL = 'https://mavrk.gitbook.io/sentry-docs/sentry-token/sentry-token-sentry';
const SENTRY_LP_LOCK_URL = 'https://solscan.io/tx/Jfi7MnjZgxDeDuNivurrQNiGDvqKbQJ73DbanxhLjnsCdVrg3TSMg2sigYZ2Fzd6UXej4TB7XX6C4rW5vQZCYng';

const PageContainer = styled.div`
  padding: 16px;
  background: #000000;
  min-height: calc(100vh - 80px);
  width: 100%;
  padding-bottom: 100px;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const HeroSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  margin-bottom: 32px;
  
  @media (max-width: 1400px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCard = styled.div<{ $featured?: boolean }>`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;
  height: 470px;
  display: flex;
  flex-direction: column;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 768px) {
    height: auto;
    min-height: 200px;
  }
`;

const PortfolioHeroCard = styled(UserGroupPortfolio)`
  margin-bottom: 0;
  height: 470px;
  overflow: auto;
`;

const SentryTokenContainer = styled.div`
  display: flex;
  gap: 24px;
  height: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const SentryTokenLogoWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SentryTokenLogo = styled.img`
  width: 220px;
  height: 220px;
  border-radius: 50%;
  object-fit: cover;
  background: #000000;
  
  @media (max-width: 768px) {
    width: 150px;
    height: 150px;
  }
`;

const SentryTokenContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding-top: 24px;
`;

const SentryTokenNameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  width: 100%;
`;

const SentryTokenName = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
`;

const SentryTokenSymbol = styled.div`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 12px;
`;

const SentryTokenAddress = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  font-family: monospace;
  margin-bottom: 0;
`;

const SentryTokenAddressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const CopyIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const SubtleLink = styled.a`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: #ffffff;
    text-decoration: underline;
  }
`;

const SentryTokenLink = styled(SubtleLink)`
  margin-bottom: 16px;
`;

const SentryTokenPrice = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #10b981;
  margin-bottom: 8px;
`;

const SentryTokenStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const SentryTokenStatRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
`;

const SentryTokenStatLabel = styled.span`
  color: rgba(255, 255, 255, 0.5);
`;

const SentryTokenStatValue = styled.span`
  color: #ffffff;
  font-weight: 600;
`;

const SentryTokenDescription = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.5;
  margin-bottom: 16px;
`;

const SentryTokenSocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const TradeButton = styled.button<{ $disabled?: boolean }>`
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: ${({ $disabled }) => ($disabled ? 'rgba(255, 255, 255, 0.05)' : 'linear-gradient(135deg, #14F195 0%, #10b981 100%)')};
  color: ${({ $disabled }) => ($disabled ? 'rgba(255, 255, 255, 0.4)' : '#000000')};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;

  &:hover {
    ${({ $disabled }) => (!$disabled ? 'transform: translateY(-1px); box-shadow: 0 4px 12px rgba(20, 241, 149, 0.3);' : '')}
  }
`;

const TokenLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const TokenLinkIcon = styled.img`
  width: 26px;
  height: 26px;
  object-fit: contain;
`;

const IndexFundCard = styled(HeroCard)`
  justify-content: flex-start;
  align-items: stretch;
`;

const BotTradesCard = styled(HeroCard)`
  justify-content: flex-start;
  align-items: stretch;
  margin-top: 16px;
`;

const BotTradesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BotTradesItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  text-align: center;
`;

const BotTradesLabel = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const BotTradesValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #10b981;
`;

const IndexFundSplit = styled.div`
  display: flex;
  height: 100%;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const IndexFundColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CenteredIndexFundColumn = styled(IndexFundColumn)`
  align-items: center;
  text-align: center;
`;

const IndexFundDivider = styled.div`
  width: 1px;
  background: rgba(255, 255, 255, 0.06);
  
  @media (max-width: 768px) {
    width: 100%;
    height: 1px;
  }
`;

const IndexFundIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  
  img {
    width: 20px;
    height: 20px;
  }
`;

const IndexFundTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 4px;
`;

const IndexFundSubtitle = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 12px;
`;

const IndexFundStat = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #10b981;
  margin-bottom: 8px;
`;

const IndexFundSecondaryStat = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #10b981;
  margin-bottom: 8px;
`;

const IndexFundSubsection = styled.div`
  margin-top: 12px;
`;

const IndexFundStatLabel = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 6px;
`;

const IndexFundLink = styled(SubtleLink)`
  margin-bottom: 16px;
`;

const TopAssetsContainer = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TopAssetsTitle = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const TopAssetItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TopAssetLogo = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  object-fit: cover;
  background: #000000;
  border: 1px solid #333333;
`;

const TopAssetInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const TopAssetName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TopAssetSymbol = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
`;

const TopAssetValue = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #10b981;
  white-space: nowrap;
`;

const ListSectionContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const LiveFeedContainer = styled.div`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  overflow: hidden;
`;

const LiveFeedHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const LiveFeedTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
`;

const LiveFeedIndicator = styled.div<{ $active?: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? '#10b981' : 'rgba(255, 255, 255, 0.3)')};
  box-shadow: ${({ $active }) => ($active ? '0 0 10px rgba(16, 185, 129, 0.6)' : 'none')};
`;

const SwapFeedList = styled.div`
  padding: 12px 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 360px;
  overflow-y: auto;
`;

const SwapFeedItem = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(5, 5, 8, 0.4);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.04);
`;

const SwapFeedText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  flex: 1;
`;

const SwapFeedMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SwapFeedIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const SwapFeedTime = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
`;

const ensureHttpsUrl = (url: string | null | undefined): string => {
  if (!url) return '/token-placeholder.png';
  if (url.startsWith('Qm') && url.length === 46) {
    return `https://ipfs.io/ipfs/${url}`;
  }
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};

const normalizeWebsiteUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `https://${url}`;
};

const normalizeTwitterUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `https://twitter.com/${url.replace('@', '')}`;
};

const normalizeTelegramUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `https://t.me/${url.replace('@', '')}`;
};

const getTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  
  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString();
};

const toNumber = (value: unknown): number => {
  if (value === null || value === undefined) return 0;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const SOLSCAN_API_BASE = 'https://pro-api.solscan.io/v2.0';
const SOLSCAN_API_KEY = (import.meta.env as any).VITE_SOLSCAN_API_KEY as string | undefined;
const SOL_MINTS = new Set([
  'So11111111111111111111111111111111111111112',
  'So11111111111111111111111111111111111111111'
]);

type SolscanPortfolioItem = {
  token_address?: string;
  mint_address?: string;
  address?: string;
  tokenAddress?: string;
  name?: string;
  token_name?: string;
  tokenName?: string;
  symbol?: string;
  token_symbol?: string;
  tokenSymbol?: string;
  logo_url?: string;
  token_icon?: string;
  icon?: string;
  amount?: number | string;
  token_amount?: number | string;
  balance?: number | string;
  ui_amount?: number | string;
  uiAmount?: number | string;
  price?: number | string;
  price_usd?: number | string;
  usd_value?: number | string;
  value?: number | string;
  underlying?: Array<Record<string, unknown>>;
};

const getSolscanHeaders = (): HeadersInit => {
  const headers: Record<string, string> = {
    accept: 'application/json'
  };
  if (SOLSCAN_API_KEY) {
    headers.token = SOLSCAN_API_KEY;
    headers.Authorization = `Bearer ${SOLSCAN_API_KEY}`;
    headers['x-api-key'] = SOLSCAN_API_KEY;
  }
  return headers;
};

const extractUnderlyingUsdValue = (underlying?: Array<Record<string, unknown>>): number | null => {
  if (!Array.isArray(underlying) || underlying.length === 0) return null;
  const total = underlying.reduce((sum, item) => {
    const value = toNumber(item?.usd_value ?? item?.value);
    if (value > 0) return sum + value;
    const amount = toNumber(item?.amount ?? item?.token_amount ?? item?.ui_amount ?? item?.balance);
    const price = toNumber(item?.price ?? item?.price_usd);
    return sum + (amount > 0 && price > 0 ? amount * price : 0);
  }, 0);
  return total > 0 ? total : null;
};

const fetchSolscanPortfolio = async (walletAddress: string): Promise<Array<{
  mint: string;
  balance: number;
  usdValue: number;
  name: string;
  symbol: string;
  logoUrl: string | null;
  isLp: boolean;
  lpUsdValue: number | null;
}>> => {
  const response = await fetch(`${SOLSCAN_API_BASE}/account/portfolio?address=${walletAddress}`, {
    method: 'GET',
    headers: getSolscanHeaders()
  });

  if (!response.ok) {
    throw new Error(`Solscan portfolio request failed: ${response.status}`);
  }

  const payload = await response.json();
  const items: SolscanPortfolioItem[] = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];

  return items
    .map((item) => {
      const mint = item.token_address || item.mint_address || item.address || item.tokenAddress || '';
      const balance =
        toNumber(item.ui_amount ?? item.uiAmount ?? item.amount ?? item.token_amount ?? item.balance);
      const underlyingUsdValue = extractUnderlyingUsdValue(item.underlying);
      const directUsdValue = toNumber(item.usd_value ?? item.value);
      const price = toNumber(item.price ?? item.price_usd);
      const usdValue =
        underlyingUsdValue ??
        (directUsdValue > 0 ? directUsdValue : balance > 0 && price > 0 ? balance * price : 0);
      const name = item.token_name || item.tokenName || item.name || 'Unknown Token';
      const symbol = item.token_symbol || item.tokenSymbol || item.symbol || 'UNK';
      const logoUrl = item.token_icon || item.logo_url || item.icon || null;

      return {
        mint,
        balance,
        usdValue,
        name,
        symbol,
        logoUrl,
        isLp: underlyingUsdValue !== null,
        lpUsdValue: underlyingUsdValue
      };
    })
    .filter((item) => item.mint && item.balance > 0)
    .filter((item) => !SOL_MINTS.has(item.mint) && item.symbol !== 'SOL');
};

const getSwapTextColor = (message: string): string => {
  const lowerMessage = message.toLowerCase();
  if (lowerMessage.includes('$sentry') && lowerMessage.includes('bought')) {
    return '#FFD700';
  }
  if (lowerMessage.includes("dca'd") || lowerMessage.includes("dca") || lowerMessage.includes('called')) {
    return '#10b981';
  }
  if (lowerMessage.includes('sold') || lowerMessage.includes('took profit')) {
    return '#ef4444';
  }
  return 'rgba(255, 255, 255, 0.8)';
};

const formatUsdAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '--';
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const getSwapIconSource = (swap: SwapEvent): string | null => {
  const source = swap.data?.source;
  if (source === 'pwa') return PWA_SWAP_ICON_URL;
  if (source === 'telegram') return TELEGRAM_SWAP_ICON_URL;
  if (source === 'sentry_buy') return SENTRY_SWAP_ICON_URL;

  const lowerMessage = swap.message.toLowerCase();
  if (lowerMessage.includes('$sentry') && lowerMessage.includes('bought')) {
    return SENTRY_SWAP_ICON_URL;
  }
  if (
    lowerMessage.includes("dca'd") ||
    lowerMessage.includes('swapped') ||
    lowerMessage.includes('took profit') ||
    lowerMessage.includes('called')
  ) {
    return TELEGRAM_SWAP_ICON_URL;
  }
  if (swap.type === 'buy' || swap.type === 'sell') {
    return PWA_SWAP_ICON_URL;
  }
  return null;
};

const getSolanaConnection = (): Connection => {
  const rpcUrl = (import.meta.env as any).VITE_SOLANA_RPC_URL || 'https://solana.publicnode.com';
  return new Connection(rpcUrl, 'confirmed');
};

const SentryStatsPWA: React.FC = () => {
  const [sentryToken, setSentryToken] = useState<any>(null);
  const [sentryTokenPrice, setSentryTokenPrice] = useState<number | null>(null);
  const [sentryTokenVolume24h, setSentryTokenVolume24h] = useState<number | null>(null);
  const [sentryTvlUsd, setSentryTvlUsd] = useState<number | null>(null);
  const [sentryTmsUsd, setSentryTmsUsd] = useState<number | null>(null);
  const [sentryTvlLoading, setSentryTvlLoading] = useState(true);
  const [feeSwapSummary, setFeeSwapSummary] = useState<SentryFeeSwapSummary | null>(null);
  const [indexFundValue, setIndexFundValue] = useState<{
    sol: number;
    usd: number;
    tokenCount: number;
    lpUsd: number;
  } | null>(null);
  const [topAssets, setTopAssets] = useState<Array<{
    mint: string;
    balance: number;
    usdValue: number;
    name: string;
    symbol: string;
    logoUrl: string | null;
  }>>([]);
  const [recentSwaps, setRecentSwaps] = useState<SwapEvent[]>([]);
  const [telegramUser, setTelegramUser] = useState<PwaAuthUser | TelegramUser | null>(null);
  const navigate = useNavigate();
  const { snsName: sentryTokenDomain } = useSNSName(
    telegramUser ? SENTRY_TOKEN_ADDRESS : undefined,
    Boolean(telegramUser)
  );

  const handleCopySentryAddress = async () => {
    try {
      await navigator.clipboard.writeText(SENTRY_TOKEN_ADDRESS);
    } catch (error) {
      console.error('Failed to copy SENTRY address:', error);
    }
  };

  const handleTradeClick = (token: any) => {
    const mintAddress = token.contract_address || token.mint_address;
    if (!mintAddress || !token.pool_address) {
      return;
    }
    navigate(`/sentry-pwa/swap?mint=${mintAddress}`);
  };

  useEffect(() => {
    const fetchSentryToken = async () => {
      try {
        const token = await getSolanaTokenByAddress(SENTRY_TOKEN_ADDRESS);
        if (token) {
          setSentryToken(token);
          const price = await getRelayTokenPriceSolana(SENTRY_TOKEN_ADDRESS);
          setSentryTokenPrice(price);
          try {
            const dexScreenerResponse = await fetch(
              `https://api.dexscreener.com/latest/dex/tokens/${SENTRY_TOKEN_ADDRESS}`
            );
            if (dexScreenerResponse.ok) {
              const dexData = await dexScreenerResponse.json();
              if (dexData.pairs && dexData.pairs.length > 0) {
                const pair = dexData.pairs[0];
                const volume24h = pair.volume?.h24 || pair.volume24h || 0;
                setSentryTokenVolume24h(volume24h);
              }
            }
          } catch (error) {
            console.warn('Error fetching DexScreener data:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching Sentry token:', error);
      }
    };

    fetchSentryToken();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadTelegramUser = async () => {
      try {
        const user = await fetchPwaAuthUser();
        if (!cancelled) {
          setTelegramUser(user);
        }
      } catch (error) {
        if (!cancelled) {
          setTelegramUser(null);
        }
      }
    };

    loadTelegramUser();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchSentryTvl = async () => {
      try {
        const response = await fetch(`${SENTRY_BOT_API_URL}/api/tvl`);
        if (!response.ok) {
          throw new Error(`Failed to fetch TVL: ${response.status}`);
        }
        const data = await response.json();
        const totalValueUsd = typeof data?.totalValueUsd === 'number' ? data.totalValueUsd : null;
        const totalMarketSizeUsd = typeof data?.totalMarketSizeUsd === 'number' ? data.totalMarketSizeUsd : null;
        setSentryTvlUsd(totalValueUsd);
        setSentryTmsUsd(totalMarketSizeUsd);
      } catch (error) {
        console.warn('[SentryStatsPWA] Failed to fetch TVL:', error);
      } finally {
        setSentryTvlLoading(false);
      }
    };

    fetchSentryTvl();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadSummary = async () => {
      try {
        const data = await fetchSentryFeeSwapSummary();
        if (!cancelled) {
          setFeeSwapSummary(data);
        }
      } catch (error) {
        console.warn('[SentryStatsPWA] Failed to fetch fee swap summary:', error);
      }
    };
    loadSummary();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchIndexFund = async () => {
      try {
        const connection = getSolanaConnection();
        const walletPubkey = new PublicKey(SENTRY_FEES_WALLET);
        const solBalance = await connection.getBalance(walletPubkey);
        const solBalanceFormatted = solBalance / LAMPORTS_PER_SOL;
        const WSOL_ADDRESS = 'So11111111111111111111111111111111111111112';
        const solPrice = await getRelayTokenPriceSolana(WSOL_ADDRESS) || 150;
        const solValueUSD = solBalanceFormatted * solPrice;

        let totalTokenValueUSD = 0;
        let tokenCount = 0;
        let lpUsdTotal = 0;
        let topAssetsWithMetadata: Array<{
          mint: string;
          balance: number;
          usdValue: number;
          name: string;
          symbol: string;
          logoUrl: string | null;
        }> = [];

        try {
          const solscanTokens = await fetchSolscanPortfolio(walletPubkey.toBase58());
          if (solscanTokens.length > 0) {
            totalTokenValueUSD = solscanTokens.reduce((sum, token) => sum + token.usdValue, 0);
            lpUsdTotal = solscanTokens
              .filter((token) => token.isLp && token.lpUsdValue !== null)
              .reduce((sum, token) => sum + (token.lpUsdValue || 0), 0);
            tokenCount = solscanTokens.length;
            topAssetsWithMetadata = solscanTokens
              .sort((a, b) => b.usdValue - a.usdValue)
              .slice(0, 3);
          }
        } catch (error) {
          console.warn('Solscan portfolio fetch failed, falling back to on-chain pricing:', error);
        }

        if (topAssetsWithMetadata.length === 0) {
          const TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA');
          const TOKEN_2022_PROGRAM_ID = new PublicKey('TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb');

          const tokenAccounts = await connection.getParsedTokenAccountsByOwner(walletPubkey, {
            programId: TOKEN_PROGRAM_ID
          });

          const tokenAccounts2022 = await connection.getParsedTokenAccountsByOwner(walletPubkey, {
            programId: TOKEN_2022_PROGRAM_ID
          });

          const allTokenAccounts = [...tokenAccounts.value, ...tokenAccounts2022.value];
          const tokenData: Array<{ mint: string; balance: number }> = [];

          for (const account of allTokenAccounts) {
            const parsedInfo = account.account.data.parsed?.info;
            if (!parsedInfo) continue;

            const mint = parsedInfo.mint;
            const balance = parsedInfo.tokenAmount?.uiAmount || 0;

            if (balance > 0) {
              tokenCount++;
              tokenData.push({ mint, balance });
            }
          }

          const BATCH_SIZE = 5;
          const tokenValues: Array<{ mint: string; balance: number; usdValue: number }> = [];

          for (let i = 0; i < tokenData.length; i += BATCH_SIZE) {
            const batch = tokenData.slice(i, i + BATCH_SIZE);
            const batchPromises = batch.map(async ({ mint, balance }) => {
              try {
                const price = await getRelayTokenPriceSolana(mint);
                if (price && price > 0) {
                  const usdValue = balance * price;
                  tokenValues.push({ mint, balance, usdValue });
                  return usdValue;
                }
              } catch (e) {
                console.warn(`Failed to fetch price for token ${mint}:`, e);
              }
              return 0;
            });

            const batchValues = await Promise.all(batchPromises);
            totalTokenValueUSD += batchValues.reduce((sum, val) => sum + val, 0);

            if (i + BATCH_SIZE < tokenData.length) {
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          }

          const top3Tokens = tokenValues
            .sort((a, b) => b.usdValue - a.usdValue)
            .slice(0, 3);

          topAssetsWithMetadata = await Promise.all(
            top3Tokens.map(async ({ mint, balance, usdValue }) => {
              try {
                const tokenMetadata = await getSolanaTokenByAddress(mint);
                return {
                  mint,
                  balance,
                  usdValue,
                  name: tokenMetadata?.name || 'Unknown Token',
                  symbol: tokenMetadata?.symbol || 'UNK',
                  logoUrl: tokenMetadata?.logo_url || null
                };
              } catch (error) {
                console.warn(`Failed to fetch metadata for token ${mint}:`, error);
                return {
                  mint,
                  balance,
                  usdValue,
                  name: 'Unknown Token',
                  symbol: 'UNK',
                  logoUrl: null
                };
              }
            })
          );
        }

        setTopAssets(topAssetsWithMetadata);
        setIndexFundValue({
          sol: solBalanceFormatted,
          usd: solValueUSD + totalTokenValueUSD,
          tokenCount,
          lpUsd: lpUsdTotal
        });
      } catch (error) {
        console.error('Error fetching index fund:', error);
      }
    };

    fetchIndexFund();
  }, []);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const history = await fetchSwapFeedHistory(50);
        if (history.length > 0) {
          setRecentSwaps([...history].reverse());
        }
      } catch (error) {
        console.error('[SentryStatsPWA] Error loading swap history:', error);
      }
    };

    loadHistory();

    const { cleanup } = createSwapFeedConnection(
      (event) => {
        setRecentSwaps(prev => {
          const newSwaps = [event, ...prev];
          const unique = new Map();
          newSwaps.forEach(swap => {
            if (!unique.has(swap.id)) {
              unique.set(swap.id, swap);
            }
          });
          return Array.from(unique.values()).slice(0, 100);
        });
      },
      (events) => {
        if (events.length > 0) {
          setRecentSwaps([...events].reverse().slice(0, 100));
        }
      },
      (error) => {
        console.error('[SentryStatsPWA] Swap feed SSE error:', error);
      }
    );

    return () => {
      cleanup();
    };
  }, []);

  return (
    <PageContainer>
      <HeroSection>
        <HeroCard $featured>
          {sentryToken ? (
            <SentryTokenContainer>
              <SentryTokenLogoWrapper>
                <SentryTokenLogo 
                  src={ensureHttpsUrl(sentryToken.logo_url)} 
                  alt={sentryToken.name}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => { (e.target as HTMLImageElement).src = '/token-placeholder.png'; }}
                />
              </SentryTokenLogoWrapper>
              
              <SentryTokenContent>
                <SentryTokenNameRow>
                  <SentryTokenName>{sentryToken.name}</SentryTokenName>
                  <TradeButton
                    onClick={() => handleTradeClick(sentryToken)}
                    $disabled={!sentryToken.pool_address}
                    disabled={!sentryToken.pool_address}
                  >
                    {sentryToken.pool_address ? 'Trade' : 'No Pool'}
                  </TradeButton>
                </SentryTokenNameRow>
                <SentryTokenSymbol>${sentryToken.symbol}</SentryTokenSymbol>
                <SentryTokenAddressRow>
                  <SentryTokenAddress>
                    {sentryTokenDomain || `${SENTRY_TOKEN_ADDRESS.slice(0, 8)}...${SENTRY_TOKEN_ADDRESS.slice(-6)}`}
                  </SentryTokenAddress>
                  <CopyIconButton
                    type="button"
                    onClick={handleCopySentryAddress}
                    title="Copy contract address"
                    aria-label="Copy SENTRY contract address"
                  >
                    <Copy size={12} />
                  </CopyIconButton>
                </SentryTokenAddressRow>
                <SentryTokenLink href={SENTRY_TOKEN_SOLSCAN_URL} target="_blank" rel="noopener noreferrer">
                  View on Solscan
                </SentryTokenLink>
                {sentryTokenPrice !== null && (
                  <SentryTokenPrice>
                    {formatPriceDisplay(sentryTokenPrice)}
                  </SentryTokenPrice>
                )}
                <SentryTokenStats>
                  {sentryTokenVolume24h !== null && sentryTokenVolume24h > 0 && (
                    <SentryTokenStatRow>
                      <SentryTokenStatLabel>24h Volume</SentryTokenStatLabel>
                      <SentryTokenStatValue>
                        ${sentryTokenVolume24h >= 1000 
                          ? (sentryTokenVolume24h / 1000).toFixed(2) + 'K'
                          : sentryTokenVolume24h.toLocaleString('en-US', { 
                              minimumFractionDigits: 2, 
                              maximumFractionDigits: 2 
                            })
                        }
                      </SentryTokenStatValue>
                    </SentryTokenStatRow>
                  )}
                </SentryTokenStats>
                <SentryTokenDescription>
                  {sentryToken.description || 'The native token of the Sentry Trading Bot ecosystem'}
                </SentryTokenDescription>
                <SentryTokenSocialLinks>
                  {normalizeWebsiteUrl(sentryToken.website) && (
                    <TokenLink href={normalizeWebsiteUrl(sentryToken.website) || undefined} target="_blank" rel="noopener noreferrer">
                      <TokenLinkIcon src={WEBSITE_ICON_URL} alt="Website" />
                    </TokenLink>
                  )}
                  <TokenLink
                    href={`https://dexscreener.com/solana/${SENTRY_TOKEN_ADDRESS}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <TokenLinkIcon src={DEXSCREENER_ICON_URL} alt="Dexscreener" />
                  </TokenLink>
                  <TokenLink
                    href={`https://www.orca.so/?tokenIn=So11111111111111111111111111111111111111112&tokenOut=${encodeURIComponent(SENTRY_TOKEN_ADDRESS)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <TokenLinkIcon src={ORCA_ICON_URL} alt="Orca buy" />
                  </TokenLink>
                  {normalizeTwitterUrl(sentryToken.twitter) && (
                    <TokenLink href={normalizeTwitterUrl(sentryToken.twitter) || undefined} target="_blank" rel="noopener noreferrer">
                      <TokenLinkIcon src={TWITTER_ICON_URL} alt="Twitter" />
                    </TokenLink>
                  )}
                  {normalizeTelegramUrl(sentryToken.telegram) && (
                    <TokenLink href={normalizeTelegramUrl(sentryToken.telegram) || undefined} target="_blank" rel="noopener noreferrer">
                      <TokenLinkIcon src={TELEGRAM_ICON_URL} alt="Telegram" />
                    </TokenLink>
                  )}
                  <TokenLink href={SENTRY_GITBOOK_URL} target="_blank" rel="noopener noreferrer">
                    <TokenLinkIcon src={GITBOOK_ICON_URL} alt="Gitbook" />
                  </TokenLink>
                  <TokenLink href={SENTRY_LP_LOCK_URL} target="_blank" rel="noopener noreferrer">
                    <TokenLinkIcon src={LP_LOCK_ICON_URL} alt="Liquidity lock" />
                  </TokenLink>
                </SentryTokenSocialLinks>
              </SentryTokenContent>
            </SentryTokenContainer>
          ) : (
            <EmptyState>Loading Sentry token...</EmptyState>
          )}
        </HeroCard>
        
        <IndexFundCard>
          <IndexFundSplit>
            <CenteredIndexFundColumn>
              <IndexFundIcon>
                <img src={TVL_ICON_URL} alt="TVL" />
              </IndexFundIcon>
              <IndexFundTitle>Sentry Ecosystem TVL</IndexFundTitle>
              <IndexFundSubtitle>Total Value Locked</IndexFundSubtitle>
              <IndexFundStat>
                {sentryTvlLoading ? '--' : formatUsdAmount(sentryTvlUsd)}
              </IndexFundStat>
              <IndexFundSubsection>
                <IndexFundTitle>Sentry Ecosystem TMS</IndexFundTitle>
                <IndexFundSubtitle>Total Market Size</IndexFundSubtitle>
                <IndexFundSecondaryStat>
                  {sentryTvlLoading ? '--' : formatUsdAmount(sentryTmsUsd)}
                </IndexFundSecondaryStat>
              </IndexFundSubsection>
            </CenteredIndexFundColumn>
            <IndexFundDivider />
            <CenteredIndexFundColumn>
              <IndexFundIcon>
                <img src={IFA_ICON_URL} alt="Index Fund Assets" />
              </IndexFundIcon>
              <IndexFundTitle>Index Fund Assets</IndexFundTitle>
              <IndexFundSubtitle>Fees & Holdings Wallet</IndexFundSubtitle>
              <IndexFundLink href={SENTRY_FEES_WALLET_SOLSCAN_URL} target="_blank" rel="noopener noreferrer">
                View on Solscan
              </IndexFundLink>
              {indexFundValue ? (
                <>
                  <IndexFundStat>
                    ${indexFundValue.usd.toLocaleString()}
                  </IndexFundStat>
                  <IndexFundStatLabel>
                    {indexFundValue.sol.toFixed(2)} SOL • {indexFundValue.tokenCount} tokens
                  </IndexFundStatLabel>
                  {indexFundValue.lpUsd > 0 && (
                    <IndexFundStatLabel>
                      LP Underlying Value: ${indexFundValue.lpUsd.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </IndexFundStatLabel>
                  )}
                  {topAssets.length > 0 && (
                    <TopAssetsContainer>
                      <TopAssetsTitle>Top Holdings</TopAssetsTitle>
                      {topAssets.map((asset) => (
                        <TopAssetItem key={asset.mint}>
                          {asset.logoUrl ? (
                            <TopAssetLogo 
                              src={ensureHttpsUrl(asset.logoUrl) || ''} 
                              alt={asset.symbol}
                              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <TopAssetLogo 
                              src="/token-placeholder.png" 
                              alt={asset.symbol}
                            />
                          )}
                          <TopAssetInfo>
                            <TopAssetName>{asset.name}</TopAssetName>
                            <TopAssetSymbol>${asset.symbol}</TopAssetSymbol>
                          </TopAssetInfo>
                          <TopAssetValue>
                            ${asset.usdValue.toLocaleString('en-US', { 
                              minimumFractionDigits: 2, 
                              maximumFractionDigits: 2 
                            })}
                          </TopAssetValue>
                        </TopAssetItem>
                      ))}
                    </TopAssetsContainer>
                  )}
                </>
              ) : (
                <EmptyState>Loading fund data...</EmptyState>
              )}
            </CenteredIndexFundColumn>
          </IndexFundSplit>
        </IndexFundCard>
        <BotTradesCard>
          <BotTradesGrid>
            <BotTradesItem>
              <BotTradesLabel>Sentry Bot Trades</BotTradesLabel>
              <BotTradesValue>{feeSwapSummary?.totalBuys?.toLocaleString() ?? '—'}</BotTradesValue>
            </BotTradesItem>
            <BotTradesItem>
              <BotTradesLabel>$Sentry Buy Backs</BotTradesLabel>
              <BotTradesValue>
                ${feeSwapSummary?.totalSentryBoughtUsd?.toFixed(2) ?? '—'}
              </BotTradesValue>
            </BotTradesItem>
            <BotTradesItem>
              <BotTradesLabel>$Sentry Price</BotTradesLabel>
              <BotTradesValue>
                ${feeSwapSummary?.sentryPriceUsd?.toFixed(6) ?? '—'}
              </BotTradesValue>
            </BotTradesItem>
          </BotTradesGrid>
        </BotTradesCard>

        <ComprehensiveAnalytics />
        <PortfolioHeroCard telegramUser={telegramUser} />
      </HeroSection>

      <ListSectionContainer>
        <Top5Groups />
        <TraderAnalytics />
      </ListSectionContainer>

      <LiveFeedContainer>
        <LiveFeedHeader>
          <LiveFeedTitle>
            <LiveFeedIndicator $active={recentSwaps.length > 0} />
            Live Swap Feed
          </LiveFeedTitle>
        </LiveFeedHeader>
        <SwapFeedList>
          {recentSwaps.length > 0 ? (
            recentSwaps.map((swap) => {
              const timeAgo = getTimeAgo(new Date(swap.timestamp));
              const textColor = getSwapTextColor(swap.message);
              const lowerMessage = swap.message.toLowerCase();
              const isGreen = lowerMessage.includes("dca'd") || lowerMessage.includes("dca") || lowerMessage.includes('called');
              const isRed = lowerMessage.includes('sold') || lowerMessage.includes('took profit');
              const isGold = lowerMessage.includes('$sentry') && lowerMessage.includes('bought');
              const username = swap.data?.username || swap.username;
              const tokenSymbol = swap.data?.tokenSymbol || swap.tokenSymbol;
              const tokenAmount = swap.data?.tokenAmount ?? swap.tokenAmount;
              const isTelegramMessage = lowerMessage.includes('swapped') || lowerMessage.includes('took profit') || lowerMessage.includes('called') || lowerMessage.includes("dca'd");
              const isPwaSwap = swap.data?.source === 'pwa' ||
                ((swap.type === 'buy' || swap.type === 'sell') && username && tokenSymbol && !isTelegramMessage);
              const pwaAction = swap.type === 'sell' ? 'sold' : 'bought';
              const tokenAmountLabel = tokenAmount !== undefined && tokenAmount !== null
                ? tokenAmount.toLocaleString('en-US', { maximumFractionDigits: 6 })
                : null;
              const iconSource = getSwapIconSource(swap);
              
              return (
                <SwapFeedItem 
                  key={swap.id} 
                  style={{ 
                    borderLeftColor: isGreen ? '#10b981' : isRed ? '#ef4444' : isGold ? '#FFD700' : 'transparent', 
                    borderLeftWidth: (isGreen || isRed || isGold) ? '3px' : '0px' 
                  }}
                >
                  <SwapFeedText style={{ color: textColor }}>
                    {isPwaSwap ? (
                      <SwapFeedMessage>
                        <SwapFeedIcon src={PWA_SWAP_ICON_URL} alt="PWA swap" />
                        <span>
                          {username} {pwaAction}
                          {tokenAmountLabel ? ` ${tokenAmountLabel}` : ''} {tokenSymbol}
                        </span>
                      </SwapFeedMessage>
                    ) : iconSource ? (
                      <SwapFeedMessage>
                        <SwapFeedIcon src={iconSource} alt="Swap source" />
                        <span>{swap.message}</span>
                      </SwapFeedMessage>
                    ) : (
                      swap.message
                    )}
                  </SwapFeedText>
                  <SwapFeedTime>{timeAgo}</SwapFeedTime>
                </SwapFeedItem>
              );
            })
          ) : (
            <EmptyState>
              No swaps yet. Swaps will appear here as they happen!
              <br />
              <small style={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '11px', marginTop: '8px', display: 'block' }}>
                Waiting for swap events from /api/swap-feed...
              </small>
            </EmptyState>
          )}
        </SwapFeedList>
      </LiveFeedContainer>
    </PageContainer>
  );
};

export default SentryStatsPWA;
